import { getContext, setContext } from 'svelte';
import type { SearchEntry } from './services/products';
import type { Category } from './models/category';

/**
 * Catalogue made available to every component without prop-drilling.
 *
 * The names are kept from the strain site so its Header/Footer/SearchBar work
 * unchanged, but the data behind them is now WooCommerce-driven.
 *
 * **The products context carries `SearchEntry`, not `Product`.** Its one
 * consumer is the header's type-ahead, and the layout that fills it runs on
 * every page — so a full `Product[]` here was ~200 KB of gallery images and
 * pack sizes serialised into every document on the site to power a search box
 * that reads a title, a path and one thumbnail. Routes that need whole products
 * (shop, category, product) load them in their own `load`.
 *
 * Getters are stored rather than arrays. `setContext` may only run during
 * component initialisation, so the layout cannot re-set the context when its
 * `data` prop changes; passing the array directly would freeze every consumer
 * on the catalogue that happened to be loaded first. Consumers call the getter
 * inside `$derived` so reads track the layout's current `data`.
 */
const productsContextKey = Symbol('products');
const categoriesContextKey = Symbol('categories');

export const setProductsContext = (products: () => SearchEntry[]) => {
	setContext(productsContextKey, products);
};

/** Call during init; invoke the result inside `$derived`. */
export const getProductsContext = (): (() => SearchEntry[]) => {
	return getContext<() => SearchEntry[]>(productsContextKey) ?? (() => []);
};

/** Alias retained for components carried over from the strain site. */
export const getSeedsContext = getProductsContext;

export const setCategoriesContext = (categories: () => Category[]) => {
	setContext(categoriesContextKey, categories);
};

/** Call during init; invoke the result inside `$derived`. */
export const getCategoriesContext = (): (() => Category[]) => {
	return getContext<() => Category[]>(categoriesContextKey) ?? (() => []);
};
